import { REQ } from "constants/api/requests"
import { IResponse } from "constants/interfaces"

import useApi, { parseError } from "util/api"

export interface BetbyDbTemplate {
  id: string
  betby_id: string
  name: string
  type: "freebet" | "comboboost"
  is_active: boolean
  is_enabled: boolean
  default_amount_cents: number | null
  default_currency: string | null
  max_bonus_number: number | null
  days_to_use: number | null
  from_time: string | null
  to_time: string | null
  freebet_data: {
    type: string // 'free_money' | 'snr' | 'bet_refund'
    amount_list: number[] | null
    min_odd: number | null
    max_odd: number | null
    min_selection: number | null
    max_selection: number | null
    is_api_amount: boolean
    bonus_refund: boolean
  } | null
  comboboost_data: {
    min_odd: number
    min_selection: number
    multipliers: string[]
    is_global: boolean
  } | null
  descriptions: Record<string, string> | null
  last_synced_at: string | null
  meta: Record<string, any> | null
}

export interface BetbyBonusRecord {
  id: string
  betby_bonus_id: string | null
  template_id: string
  template_name: string | null
  bonus_type: string
  amount_cents: number
  currency: string
  status: "ISSUED" | "ACTIVE" | "USED" | "EXPIRED" | "REVOKED" | "ERROR"
  issued_at: string
  expires_at: string | null
  activated_at: string | null
  user: { id: string; email_address: string; username: string }
}

export interface GiveBonusResult {
  userId: string
  status: "ok" | "error"
  betbyBonusId?: string
  error?: string
}

export const BetbyBonusService = {
  async syncTemplates(): Promise<{ synced: number; created: number; updated: number }> {
    try {
      const { data } = await useApi.post<IResponse<any>>(REQ.ADMIN_DASHBOARD.BETBY_BONUS.TEMPLATES_SYNC)
      return data?.success ? data.response : { synced: 0, created: 0, updated: 0 }
    } catch (error: any) {
      parseError(error)
      return { synced: 0, created: 0, updated: 0 }
    }
  },

  async getTemplates(enabledOnly = false): Promise<BetbyDbTemplate[]> {
    try {
      const { data } = await useApi.get<IResponse<any>>(REQ.ADMIN_DASHBOARD.BETBY_BONUS.TEMPLATES, {
        params: enabledOnly ? { enabledOnly: "true" } : undefined
      })
      return data?.success ? data.response ?? [] : []
    } catch (error: any) {
      parseError(error)
      return []
    }
  },

  async configureTemplate(
    id: string,
    config: { is_enabled?: boolean; default_amount_cents?: number | null; default_currency?: string | null }
  ): Promise<BetbyDbTemplate | null> {
    try {
      const { data } = await useApi.patch<IResponse<any>>(REQ.ADMIN_DASHBOARD.BETBY_BONUS.TEMPLATE(id), config)
      return data?.success ? data.response : null
    } catch (error: any) {
      parseError(error)
      return null
    }
  },

  async listBonuses(params: {
    page?: number
    pageSize?: number
    userId?: string
    status?: string
  }): Promise<{ items: BetbyBonusRecord[]; total: number }> {
    try {
      const { data } = await useApi.get<IResponse<any>>(REQ.ADMIN_DASHBOARD.BETBY_BONUS.LIST, { params })
      return data?.success ? data.response : { items: [], total: 0 }
    } catch (error: any) {
      parseError(error)
      return { items: [], total: 0 }
    }
  },

  async giveBonus(payload: {
    templateId: string
    players: Array<{ userId: string; currency: string; amountCents: number; forceActivated?: boolean }>
  }): Promise<GiveBonusResult[]> {
    try {
      const { data } = await useApi.post<IResponse<any>>(REQ.ADMIN_DASHBOARD.BETBY_BONUS.GIVE, payload)
      if (!data?.success) return []
      const details: Array<{ userId: string; status: string; betbyBonusId: string | null; error: string | null }> =
        data.response?.details ?? []
      return details.map((d) => ({
        userId: d.userId,
        status: d.status === "ISSUED" ? ("ok" as const) : ("error" as const),
        betbyBonusId: d.betbyBonusId ?? undefined,
        error: d.error ?? undefined
      }))
    } catch (error: any) {
      parseError(error)
      return []
    }
  },

  async revokeBonus(bonusIds: string[]): Promise<boolean> {
    try {
      const { data } = await useApi.post<IResponse<any>>(REQ.ADMIN_DASHBOARD.BETBY_BONUS.REVOKE, { bonusIds })
      return !!data?.success
    } catch (error: any) {
      parseError(error)
      return false
    }
  }
}
